import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  CloudUpload,
  Delete,
  Download,
  FactCheck,
  GroupAdd,
  Edit,
  Launch,
  Storefront,
} from '@mui/icons-material';
import { API_BASE } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatCpf } from '../utils/masks';

const statusLabels = {
  enviado: 'Enviado',
  em_analise: 'Em análise',
  pendente_correcao: 'Pendente de correção',
  aprovado: 'Aprovado',
  integracao_agendada: 'Integração agendada',
  liberado: 'Liberado para atividades',
};

const statusColors = {
  enviado: 'info',
  em_analise: 'warning',
  pendente_correcao: 'error',
  aprovado: 'success',
  integracao_agendada: 'secondary',
  liberado: 'success',
};

const flowSteps = [
  'Solicitação de prestação de serviço',
  'Acesso ao portal da empreiteira',
  'Cadastro da empresa e dos funcionários',
  'Envio da documentação obrigatória',
  'Análise interna e correções, quando necessário',
  'Aprovação, integração de segurança e liberação final',
];

const DOCUMENTO_FUNCIONARIO_ORDER = new Map([
  ['Ficha de Registro', 0],
  ['Ordem de Serviço', 1],
  ['ASO', 2],
  ['Ficha de EPIs', 3],
]);

const TREINAMENTO_FUNCIONARIO_ORDER = new Map([
  ['NR 06', 0],
  ['NR 10', 1],
  ['NR 12', 2],
  ['NR 35', 3],
]);

const VENCIMENTO_ALERTA_DIAS = 30;
const EMPRESA_DOCUMENTOS_VALIDADE = [
  { label: 'PGR', anos: 2 },
  { label: 'PCMSO', anos: 1 },
];

const emptyReview = {
  pendencia_motivo: '',
  observacoes_internas: '',
  integracao_agendada_para: '',
  link_documentacao: '',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return value;
  }
};

const formatDateBr = (value) => {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }

  try {
    return new Date(value).toLocaleDateString('pt-BR');
  } catch {
    return value;
  }
};

const getExpirationStatusMeta = (value) => {
  if (!value) {
    return { label: 'Sem vencimento informado', color: 'text.secondary' };
  }

  const reference = new Date(`${value}T00:00:00`);
  if (Number.isNaN(reference.getTime())) {
    return { label: 'Vencimento inválido', color: 'warning.main' };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffInDays = Math.round((reference.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffInDays < 0) {
    return { label: `Vencido há ${Math.abs(diffInDays)} dia(s)`, color: 'error.main' };
  }

  if (diffInDays <= VENCIMENTO_ALERTA_DIAS) {
    return { label: `Vence em ${diffInDays} dia(s)`, color: 'warning.main' };
  }

  return { label: 'Em dia', color: 'success.main' };
};

const sortStructuredFuncionarioDocumentos = (documentos = []) =>
  [...documentos].sort((left, right) => {
    const leftOrder = DOCUMENTO_FUNCIONARIO_ORDER.get(left?.tipo_doc) ?? 999;
    const rightOrder = DOCUMENTO_FUNCIONARIO_ORDER.get(right?.tipo_doc) ?? 999;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;

    return String(left?.data_vencimento || '').localeCompare(String(right?.data_vencimento || ''));
  });

const sortStructuredTreinamentos = (treinamentos = []) =>
  [...treinamentos].sort((left, right) => {
    const leftOrder = TREINAMENTO_FUNCIONARIO_ORDER.get(left?.treinamento_nome) ?? 999;
    const rightOrder = TREINAMENTO_FUNCIONARIO_ORDER.get(right?.treinamento_nome) ?? 999;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;

    return String(left?.data_realizacao || '').localeCompare(String(right?.data_realizacao || ''));
  });

const getDownloadDocumentId = (documento) => documento?.arquivo?.id || documento?.id || null;

const getFilenameFromDisposition = (contentDisposition, fallback = 'documento') => {
  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || fallback;
};

const formatCpfDisplay = (value) => {
  const formatted = formatCpf(value || '');
  return formatted || '-';
};

const formatShortDateBr = (value) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('pt-BR');
  } catch {
    return '';
  }
};

const addYearsToDate = (value, years) => {
  if (!value || !years) return '';

  const reference = new Date(value);
  if (Number.isNaN(reference.getTime())) return '';

  reference.setFullYear(reference.getFullYear() + years);
  const year = reference.getFullYear();
  const month = String(reference.getMonth() + 1).padStart(2, '0');
  const day = String(reference.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getEmpresaDocumentoMeta = (documento, index) => {
  const config = EMPRESA_DOCUMENTOS_VALIDADE[index] || null;
  const uploadedDate =
    documento?.uploaded_at || documento?.criado_em || documento?.created_at || '';
  const validUntil = config ? addYearsToDate(uploadedDate, config.anos) : '';
  const expiration = config ? getExpirationStatusMeta(validUntil) : null;

  return {
    label: config?.label || documento?.original_name || 'Documento',
    anos: config?.anos || 0,
    validUntil,
    expiration,
  };
};

const formatFuncionarioDocumentLabel = (documento) => {
  const colaboradorNome = documento?.funcionario_nome || '';
  const colaboradorCpf = formatCpf(documento?.funcionario_cpf || '');
  const itemLabel =
    documento?.categoria === 'treinamento'
      ? documento?.treinamento_nome || 'Treinamento'
      : documento?.tipo_doc || 'Documento';

  return [colaboradorNome, colaboradorCpf, itemLabel, documento?.original_name]
    .filter(Boolean)
    .join(' • ');
};

function MetricCard({ title, value, subtitle }) {
  return (
    <Card sx={{ borderRadius: 4, height: '100%', boxShadow: '0 16px 38px rgba(15,23,42,0.08)' }}>
      <CardContent>
        <Typography variant="overline" sx={{ color: '#4f46e5', fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, mt: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function ProcessoCadastroEmpreiteira() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState(emptyReview);
  const [busyAction, setBusyAction] = useState('');
  const [feedback, setFeedback] = useState({ type: 'info', message: '' });
  const [historico, setHistorico] = useState([]);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [validacao, setValidacao] = useState({ checklist: [], resultado: null });
  const isAdmin = ['admin', 'administrador', 'willians'].includes(
    (user?.nivel_acesso || '').toLowerCase()
  );

  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ razao_social: '', status: '', integracao_status: '' });
  const [editBusy, setEditBusy] = useState(false);

  const [deletedItems, setDeletedItems] = useState([]);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      razao_social: item.razao_social || '',
      status: item.status || '',
      integracao_status: item.integracao_status || 'pendente',
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    try {
      setEditBusy(true);
      await apiRequest(`/cadastro-empreiteiras/${editItem.id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm),
      });
      setEditOpen(false);
      setFeedback({ type: 'success', message: 'Processo atualizado com sucesso.' });
      await loadItems();
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao atualizar.' });
    } finally {
      setEditBusy(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Excluir o processo ${item.protocolo} de ${item.razao_social}?\nO processo ficará na aba de excluídos e pode ser restaurado.`)) return;
    try {
      await apiRequest(`/cadastro-empreiteiras/${item.id}`, { method: 'DELETE' });
      setFeedback({ type: 'success', message: 'Processo excluído.' });
      await loadItems();
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao excluir.' });
    }
  };

  const publicUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/cadastro-empreiteira-publico`
      : '/cadastro-empreiteira-publico';

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const apiRequest = useCallback(
    async (path, options = {}) => {
      const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...(options.headers || {}),
        },
      });

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        if (!data && text && text.trim().startsWith('<')) {
          throw new Error('Serviço indisponível para esta ação no momento. Tente novamente em instantes.');
        }
        throw new Error(data?.detail || data?.error || 'Erro ao processar a solicitação');
      }

      return data;
    },
    [authHeaders]
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest('/cadastro-empreiteiras');
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  const loadDeleted = useCallback(async () => {
    setLoadingDeleted(true);
    try {
      const data = await apiRequest('/cadastro-empreiteiras/deleted');
      setDeletedItems(Array.isArray(data) ? data : []);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setLoadingDeleted(false);
    }
  }, [apiRequest]);

  const openDeleted = () => {
    setDeletedOpen(true);
    loadDeleted();
  };

  const handleRestore = async (item) => {
    try {
      await apiRequest(`/cadastro-empreiteiras/${item.id}/restore`, { method: 'PATCH' });
      setFeedback({ type: 'success', message: `Processo ${item.protocolo} restaurado.` });
      await Promise.all([loadItems(), loadDeleted()]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao restaurar.' });
    }
  };

  useEffect(() => {
    loadItems();
    if (isAdmin) loadDeleted();
  }, [loadItems, loadDeleted, isAdmin]);

  const metrics = useMemo(() => {
    const total = items.length;
    const emAnalise = items.filter((item) =>
      ['enviado', 'em_analise'].includes(item.status)
    ).length;
    const pendentes = items.filter((item) => item.status === 'pendente_correcao').length;
    const aprovados = items.filter((item) =>
      ['aprovado', 'integracao_agendada', 'liberado'].includes(item.status)
    ).length;
    return { total, emAnalise, pendentes, aprovados };
  }, [items]);

  const openDetails = async (item) => {
    try {
      const data = await apiRequest(`/cadastro-empreiteiras/${item.id}`);
      setSelectedItem(data);
      setReviewForm({
        pendencia_motivo: data.pendencia_motivo || '',
        observacoes_internas: data.observacoes_internas || '',
        integracao_agendada_para: data.integracao_agendada_para
          ? data.integracao_agendada_para.slice(0, 16)
          : '',
        link_documentacao: data.link_documentacao || '',
      });
      setDetailsOpen(true);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    }
  };

  const updateStatus = async (status) => {
    if (!selectedItem) return;
    try {
      setBusyAction(status);
      const updated = await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({
          status,
          pendencia_motivo: reviewForm.pendencia_motivo,
          observacoes_internas: reviewForm.observacoes_internas,
          integracao_agendada_para: reviewForm.integracao_agendada_para || null,
        }),
      });

      setSelectedItem(updated);
      await loadItems();

      if (status === 'aprovado') {
        const shouldConvert = window.confirm(
          'Deseja incluir esta empresa no cadastro de fornecedores do sistema?'
        );
        if (shouldConvert) {
          await convertToFornecedor(updated.id);
          navigate('/fornecedores');
          return;
        }
      }

      setFeedback({
        type: 'success',
        message: `Status atualizado para ${statusLabels[status] || status}.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setBusyAction('');
    }
  };

  const convertToFornecedor = async (cadastroId = selectedItem?.id) => {
    if (!cadastroId) return;
    try {
      setBusyAction('converter');
      const result = await apiRequest(`/cadastro-empreiteiras/${cadastroId}/converter-fornecedor`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setFeedback({
        type: 'success',
        message: `Fornecedor ${result.fornecedor_nome} criado com sucesso.`,
      });
      await loadItems();
      if (selectedItem?.id === cadastroId) {
        const refreshed = await apiRequest(`/cadastro-empreiteiras/${cadastroId}`);
        setSelectedItem(refreshed);
      }
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setBusyAction('');
    }
  };

  const downloadDocument = async (documentId, fallbackName = 'documento') => {
    if (!selectedItem?.id) return;

    try {
      const response = await fetch(
        `${API_BASE}/cadastro-empreiteiras/${selectedItem.id}/documentos/${documentId}/download`,
        {
          headers: authHeaders,
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let errorPayload = null;

        try {
          errorPayload = text ? JSON.parse(text) : null;
        } catch {
          errorPayload = null;
        }

        throw new Error(
          errorPayload?.detail || errorPayload?.error || 'Não foi possível baixar o documento.'
        );
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = getFilenameFromDisposition(
        response.headers.get('content-disposition'),
        fallbackName
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.message || 'Não foi possível baixar o documento.',
      });
    }
  };

  const generateExternalEditLink = async (documentId, fallbackLabel = 'documento') => {
    if (!selectedItem?.id || !documentId) return;

    try {
      setBusyAction(`link-${documentId}`);
      const payload = await apiRequest(
        `/cadastro-empreiteiras/${selectedItem.id}/documentos/${documentId}/gerar-link-edicao`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );

      const editUrl = `${window.location.origin}/cadastro-empreiteira-documento/${payload.access_token}`;
      const expiresAt = formatDateTime(payload.expires_at);
      const label = payload.documento_label || fallbackLabel;

      try {
        await navigator.clipboard.writeText(editUrl);
        setFeedback({
          type: 'success',
          message: `Link exclusivo de atualização (${label}) copiado. Expira em ${expiresAt}.`,
        });
      } catch {
        setFeedback({
          type: 'info',
          message: `Link de atualização (${label}): ${editUrl} (expira em ${expiresAt})`,
        });
      }

      const refreshed = await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}`);
      setSelectedItem(refreshed);
      await loadItems();
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao gerar link externo.' });
    } finally {
      setBusyAction('');
    }
  };

  const deleteDocument = async (documentId, fallbackLabel = 'documento') => {
    if (!selectedItem?.id || !documentId) return;

    if (!window.confirm(`Deseja realmente excluir ${fallbackLabel}?`)) return;

    try {
      setBusyAction(`delete-${documentId}`);
      await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}/documentos/${documentId}`, {
        method: 'DELETE',
      });

      const refreshed = await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}`);
      setSelectedItem(refreshed);
      await loadItems();
      setFeedback({ type: 'success', message: `Documento ${fallbackLabel} excluído com sucesso.` });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao excluir documento.' });
    } finally {
      setBusyAction('');
    }
  };

  const copyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setFeedback({ type: 'success', message: 'Link público copiado com sucesso.' });
    } catch {
      setFeedback({ type: 'info', message: publicUrl });
    }
  };

  const copyEmpresaDocumentsExternalLink = async () => {
    if (!selectedItem?.public_token) {
      setFeedback({
        type: 'error',
        message: 'Não foi possível gerar link externo: token público indisponível.',
      });
      return;
    }

    const externalUrl =
      `${window.location.origin}/cadastro-empreiteira-publico` +
      `?token=${encodeURIComponent(selectedItem.public_token)}`;

    try {
      await navigator.clipboard.writeText(externalUrl);
      setFeedback({
        type: 'success',
        message: 'Link externo para envio de documentos da empresa copiado com sucesso.',
      });
    } catch {
      setFeedback({ type: 'info', message: externalUrl });
    }
  };

  const copyFuncionariosExternalLink = async () => {
    if (!selectedItem?.public_token) {
      setFeedback({
        type: 'error',
        message: 'Não foi possível gerar link externo: token público indisponível.',
      });
      return;
    }

    const externalUrl =
      `${window.location.origin}/cadastro-empreiteira-publico` +
      `?token=${encodeURIComponent(selectedItem.public_token)}&step=2`;

    try {
      await navigator.clipboard.writeText(externalUrl);
      setFeedback({
        type: 'success',
        message: 'Link externo para adicionar funcionários copiado com sucesso.',
      });
    } catch {
      setFeedback({ type: 'info', message: externalUrl });
    }
  };

  const carregarHistorico = async () => {
    if (!selectedItem?.id) return;
    try {
      const data = await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}/historico`);
      setHistorico(data.historico || []);
      setHistoricoOpen(true);
    } catch (error) {
      setFeedback({ type: 'error', message: `Erro ao carregar histórico: ${error.message}` });
    }
  };

  const salvarAnalise = async () => {
    if (!selectedItem?.id) return;
    try {
      setBusyAction('salvar-analise');
      await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}/salvar-analise`, {
        method: 'PUT',
        body: JSON.stringify({
          pendencia_motivo: reviewForm.pendencia_motivo || null,
          observacoes_internas: reviewForm.observacoes_internas || null,
          link_documentacao: reviewForm.link_documentacao || null,
          validacao_checklist: validacao.checklist || [],
        }),
      });

      setFeedback({
        type: 'success',
        message: 'Análise do cadastro salva com sucesso (status não alterado).',
      });
      await loadItems();
    } catch (error) {
      setFeedback({ type: 'error', message: `Erro ao salvar análise: ${error.message}` });
    } finally {
      setBusyAction('');
    }
  };

  const executarValidacao = async () => {
    if (!selectedItem?.id) return;
    try {
      setBusyAction('validar');
      const resultado = await apiRequest(`/cadastro-empreiteiras/${selectedItem.id}/validar`, {
        method: 'POST',
        body: JSON.stringify({
          validacao_checklist: validacao.checklist || [],
        }),
      });

      setValidacao((prev) => ({
        ...prev,
        resultado: resultado.validacao_automatica_resultado,
      }));

      setFeedback({
        type: resultado.validacao_pendente ? 'warning' : 'success',
        message: resultado.validacao_pendente
          ? `Validação concluída: ${Object.values(resultado.validacao_automatica_resultado).filter((v) => v === true).length} itens validados.`
          : 'Todas as validações passaram com sucesso!',
      });
    } catch (error) {
      setFeedback({ type: 'error', message: `Erro na validação: ${error.message}` });
    } finally {
      setBusyAction('');
    }
  };

  const labelTipoHistorico = (tipo) => {
    if (tipo === 'status') return 'Status';
    if (tipo === 'analise') return 'Análise';
    if (tipo === 'validacao') return 'Validação';
    return 'Evento';
  };

  const corTipoHistorico = (tipo) => {
    if (tipo === 'status') return 'primary';
    if (tipo === 'analise') return 'info';
    if (tipo === 'validacao') return 'success';
    return 'default';
  };

  const descricaoHistorico = (evento) => {
    if (evento.tipo_mudanca === 'status') {
      const anterior = evento.status_anterior || 'não informado';
      const novo = evento.status_novo || 'não informado';
      return `${anterior} → ${novo}`;
    }
    if (evento.tipo_mudanca === 'analise') {
      return 'Análise salva';
    }
    if (evento.tipo_mudanca === 'validacao') {
      return 'Validação executada';
    }
    return 'Evento registrado';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        <Paper
          sx={{
            borderRadius: 5,
            p: { xs: 2.5, md: 3.5 },
            background:
              'linear-gradient(135deg, rgba(232,244,255,1) 0%, rgba(245,248,255,1) 52%, rgba(255,245,235,1) 100%)',
            border: '1px solid rgba(99,102,241,0.12)',
          }}
        >
          <Stack spacing={2.2}>
            <Chip
              label="Processo de Cadastro de Empreiteira"
              sx={{ width: 'fit-content', fontWeight: 700 }}
            />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Fluxo interno e portal público da terceirizada
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Este módulo centraliza o fluxo do pedido de prestação de serviço, cadastro da
                empresa, envio documental, análise interna, integração de segurança e liberação
                final para atuação.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Button
                startIcon={<Launch />}
                variant="contained"
                onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              >
                Abrir portal público
              </Button>
              <Button startIcon={<CloudUpload />} variant="outlined" onClick={copyPublicUrl}>
                Copiar link do portal
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {feedback.message ? <Alert severity={feedback.type}>{feedback.message}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md>
            <MetricCard
              title="Cadastros"
              value={metrics.total}
              subtitle="Total de processos registrados"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <MetricCard
              title="Em Triagem"
              value={metrics.emAnalise}
              subtitle="Enviados ou em análise"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <MetricCard
              title="Pendências"
              value={metrics.pendentes}
              subtitle="Aguardando correção da empresa"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <MetricCard
              title="Aprovados"
              value={metrics.aprovados}
              subtitle="Aprovados, agendados ou liberados"
            />
          </Grid>
          {isAdmin && (
            <Grid item xs={12} sm={6} md>
              <Card
                onClick={openDeleted}
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  boxShadow: '0 16px 38px rgba(15,23,42,0.08)',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: deletedItems.length > 0 ? 'error.light' : 'divider',
                  '&:hover': { borderColor: 'error.main', boxShadow: '0 16px 38px rgba(239,68,68,0.15)' },
                  transition: 'all 0.2s',
                }}
              >
                <CardContent>
                  <Typography variant="overline" sx={{ color: '#ef4444', fontWeight: 700 }}>
                    Excluídos
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, mt: 0.5, color: 'error.main' }}>
                    {deletedItems.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Clique para ver e restaurar
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Paper sx={{ borderRadius: 4, p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Fluxo macro aplicado no card
          </Typography>
          <Grid container spacing={2}>
            {flowSteps.map((step, index) => (
              <Grid key={step} item xs={12} md={6} lg={4}>
                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3, height: '100%' }}>
                  <Stack spacing={1.2}>
                    <Chip
                      size="small"
                      label={`${index + 1}`}
                      color="primary"
                      sx={{ width: 'fit-content' }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {step}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper sx={{ borderRadius: 4, p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
            spacing={1.5}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Processos cadastrados
            </Typography>
            <Chip label={`${items.length} processo(s)`} color="primary" variant="outlined" />
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Protocolo</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Integração</TableCell>
                  <TableCell>Atualizado em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>Carregando processos...</TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      Nenhum cadastro de empreiteira enviado até o momento.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.protocolo}</TableCell>
                      <TableCell>{item.razao_social}</TableCell>
                      <TableCell>{item.cnpj || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusColors[item.status] || 'default'}
                          label={statusLabels[item.status] || item.status}
                        />
                      </TableCell>
                      <TableCell>{item.integracao_status || 'pendente'}</TableCell>
                      <TableCell>{formatDateTime(item.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Button size="small" onClick={() => openDetails(item)}>
                            Abrir
                          </Button>
                          {isAdmin && (
                            <>
                              <Tooltip title="Editar processo">
                                <IconButton size="small" color="primary" onClick={() => openEdit(item)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir processo">
                                <IconButton size="small" color="error" onClick={() => handleDeleteItem(item)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          {selectedItem ? `Cadastro ${selectedItem.protocolo}` : 'Cadastro de empreiteira'}
        </DialogTitle>
        <DialogContent>
          {selectedItem ? (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Alert severity={selectedItem.status === 'pendente_correcao' ? 'warning' : 'info'}>
                {selectedItem.status === 'pendente_correcao'
                  ? `Pendência registrada: ${selectedItem.pendencia_motivo || 'Aguardando ajuste da empresa.'}`
                  : `Status atual: ${statusLabels[selectedItem.status] || selectedItem.status}`}
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3, height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Empresa
                    </Typography>
                    <Stack spacing={0.8}>
                      <Typography>
                        <strong>Razão social:</strong> {selectedItem.razao_social}
                      </Typography>
                      <Typography>
                        <strong>Nome fantasia:</strong> {selectedItem.nome_fantasia || '-'}
                      </Typography>
                      <Typography>
                        <strong>CNPJ:</strong> {selectedItem.cnpj || '-'}
                      </Typography>
                      <Typography>
                        <strong>Responsável legal:</strong> {selectedItem.responsavel_legal || '-'}
                      </Typography>
                      <Typography>
                        <strong>Telefone:</strong> {selectedItem.telefone_contato || '-'}
                      </Typography>
                      <Typography>
                        <strong>E-mail:</strong> {selectedItem.email_contato || '-'}
                      </Typography>
                      <Typography>
                        <strong>Endereço:</strong> {selectedItem.endereco || '-'}
                      </Typography>
                      <Typography>
                        <strong>Atividade:</strong> {selectedItem.atividade_executada || '-'}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3, height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Solicitação do serviço
                    </Typography>
                    <Stack spacing={0.8}>
                      <Typography>
                        <strong>Unidade / setor:</strong>{' '}
                        {selectedItem.unidade_setor_solicitante || '-'}
                      </Typography>
                      <Typography>
                        <strong>Solicitante:</strong> {selectedItem.solicitante_nome || '-'}
                      </Typography>
                      <Typography>
                        <strong>E-mail do solicitante:</strong>{' '}
                        {selectedItem.solicitante_email || '-'}
                      </Typography>
                      <Typography>
                        <strong>Local de prestação:</strong> {selectedItem.local_prestacao || '-'}
                      </Typography>
                      <Typography>
                        <strong>Escopo:</strong> {selectedItem.descricao_servico || '-'}
                      </Typography>
                      <Typography>
                        <strong>Integração:</strong> {selectedItem.integracao_status || 'pendente'}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Funcionários cadastrados
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Launch />}
                    onClick={copyFuncionariosExternalLink}
                  >
                    Link externo
                  </Button>
                </Stack>
                <Stack spacing={1.1}>
                  {(selectedItem.funcionarios || []).map((funcionario, index) => {
                    const documentos = sortStructuredFuncionarioDocumentos(
                      funcionario.documentos || []
                    );
                    const treinamentos = sortStructuredTreinamentos(funcionario.treinamentos || []);

                    return (
                      <Paper
                        key={`${funcionario.nome || 'funcionario'}-${index}`}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2 }}
                      >
                        <Stack spacing={1.1}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {funcionario.nome || `Funcionário ${index + 1}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            CPF: {formatCpfDisplay(funcionario.cpf)} • Função:{' '}
                            {funcionario.funcao || '-'} • Telefone: {funcionario.telefone || '-'} •
                            E-mail: {funcionario.email || '-'}
                          </Typography>

                          {documentos.length ? (
                            <Stack spacing={0.8}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Documentos controlados
                              </Typography>
                              {documentos.map((documento) => {
                                const expirationMeta = getExpirationStatusMeta(
                                  documento.data_vencimento
                                );
                                const downloadId = getDownloadDocumentId(documento);
                                const updatedAtLabel = formatShortDateBr(
                                  documento?.arquivo?.last_updated_at || documento?.updated_at
                                );

                                return (
                                  <Stack
                                    key={`${funcionario.nome || 'funcionario'}-${documento.tipo_doc}`}
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {documento.tipo_doc} • Vencimento:{' '}
                                      {formatDateBr(documento.data_vencimento)} •{' '}
                                      <Box
                                        component="span"
                                        sx={{ color: expirationMeta.color, fontWeight: 600 }}
                                      >
                                        {expirationMeta.label}
                                      </Box>
                                      {updatedAtLabel ? ` • Atualizado em ${updatedAtLabel}` : ''}
                                    </Typography>
                                    {downloadId ? (
                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <Button
                                          size="small"
                                          startIcon={<Download />}
                                          onClick={() =>
                                            downloadDocument(
                                              downloadId,
                                              documento.arquivo?.original_name || documento.tipo_doc
                                            )
                                          }
                                        >
                                          {documento.arquivo?.original_name || 'Baixar'}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<Launch />}
                                          disabled={busyAction === `link-${downloadId}`}
                                          onClick={() =>
                                            generateExternalEditLink(
                                              downloadId,
                                              documento.tipo_doc || documento.arquivo?.original_name
                                            )
                                          }
                                        >
                                          Link externo
                                        </Button>
                                      </Stack>
                                    ) : null}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          ) : null}

                          {treinamentos.length ? (
                            <Stack spacing={0.8}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Treinamentos informados
                              </Typography>
                              {treinamentos.map((treinamento) => {
                                const expirationMeta = getExpirationStatusMeta(
                                  treinamento.data_vencimento
                                );
                                const downloadId = getDownloadDocumentId(treinamento);
                                const updatedAtLabel = formatShortDateBr(
                                  treinamento?.arquivo?.last_updated_at || treinamento?.updated_at
                                );

                                return (
                                  <Stack
                                    key={`${funcionario.nome || 'funcionario'}-${treinamento.treinamento_nome}-${treinamento.data_realizacao}`}
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {treinamento.treinamento_nome} • Realização:{' '}
                                      {formatDateBr(treinamento.data_realizacao)} • Vencimento:{' '}
                                      {formatDateBr(treinamento.data_vencimento)} •{' '}
                                      <Box
                                        component="span"
                                        sx={{ color: expirationMeta.color, fontWeight: 600 }}
                                      >
                                        {expirationMeta.label}
                                      </Box>
                                      {updatedAtLabel ? ` • Atualizado em ${updatedAtLabel}` : ''}
                                    </Typography>
                                    {downloadId ? (
                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <Button
                                          size="small"
                                          startIcon={<Download />}
                                          onClick={() =>
                                            downloadDocument(
                                              downloadId,
                                              treinamento.arquivo?.original_name ||
                                                treinamento.treinamento_nome
                                            )
                                          }
                                        >
                                          {treinamento.arquivo?.original_name ||
                                            'Baixar certificado'}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<Launch />}
                                          disabled={busyAction === `link-${downloadId}`}
                                          onClick={() =>
                                            generateExternalEditLink(
                                              downloadId,
                                              treinamento.treinamento_nome ||
                                                treinamento.arquivo?.original_name
                                            )
                                          }
                                        >
                                          Link externo
                                        </Button>
                                      </Stack>
                                    ) : null}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3, height: '100%' }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Documentos da empresa
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Launch />}
                        onClick={copyEmpresaDocumentsExternalLink}
                      >
                        Link externo
                      </Button>
                    </Stack>
                    <Stack spacing={1}>
                      {(selectedItem.documentos_empresa || []).length === 0 ? (
                        <Typography color="text.secondary">Nenhum documento enviado.</Typography>
                      ) : (
                        selectedItem.documentos_empresa.map((documento, index) => {
                          const empresaMeta = getEmpresaDocumentoMeta(documento, index);
                          const updatedAtLabel = formatShortDateBr(documento?.last_updated_at);
                          const documentLabel = empresaMeta.label;
                          const documentDescription = empresaMeta.validUntil
                            ? `Validade prevista até ${formatDateBr(empresaMeta.validUntil)}`
                            : 'Documento complementar';
                          return (
                            <Stack
                              key={documento.id}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 700 }}>{documentLabel}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {documentDescription}
                                  {empresaMeta.expiration?.label
                                    ? ` • ${empresaMeta.expiration.label}`
                                    : ''}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {documento.original_name}
                                </Typography>
                                {updatedAtLabel ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Atualizado em {updatedAtLabel}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button
                                  startIcon={<Download />}
                                  variant="outlined"
                                  onClick={() =>
                                    downloadDocument(documento.id, documento.original_name)
                                  }
                                >
                                  Baixar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={isAdmin ? <Edit /> : <Launch />}
                                  disabled={busyAction === `link-${documento.id}`}
                                  onClick={() =>
                                    generateExternalEditLink(documento.id, documento.original_name)
                                  }
                                >
                                  {isAdmin ? 'Editar' : 'Link externo'}
                                </Button>
                                {isAdmin ? (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={<Delete />}
                                      disabled={busyAction === `delete-${documento.id}`}
                                      onClick={() =>
                                        deleteDocument(documento.id, documento.original_name)
                                      }
                                    >
                                      Excluir
                                    </Button>
                                  </>
                                ) : null}
                              </Stack>
                            </Stack>
                          );
                        })
                      )}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 3, height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Documentos dos colaboradores
                    </Typography>
                    <Stack spacing={1}>
                      {(selectedItem.documentos_funcionarios || []).length === 0 ? (
                        <Typography color="text.secondary">Nenhum documento enviado.</Typography>
                      ) : (
                        selectedItem.documentos_funcionarios.map((documento) => {
                          const documentLabel = formatFuncionarioDocumentLabel(documento);
                          return (
                            <Stack
                              key={documento.id}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {documentLabel}
                              </Typography>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button
                                  startIcon={<Download />}
                                  variant="outlined"
                                  onClick={() =>
                                    downloadDocument(
                                      documento.id,
                                      documento.original_name || 'documento'
                                    )
                                  }
                                >
                                  Baixar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={isAdmin ? <Edit /> : <Launch />}
                                  disabled={busyAction === `link-${documento.id}`}
                                  onClick={() =>
                                    generateExternalEditLink(
                                      documento.id,
                                      documento.original_name || 'documento'
                                    )
                                  }
                                >
                                  {isAdmin ? 'Editar' : 'Link externo'}
                                </Button>
                                {isAdmin ? (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      startIcon={<Delete />}
                                      disabled={busyAction === `delete-${documento.id}`}
                                      onClick={() =>
                                        deleteDocument(
                                          documento.id,
                                          documento.original_name || 'documento'
                                        )
                                      }
                                    >
                                      Excluir
                                    </Button>
                                  </>
                                ) : null}
                              </Stack>
                            </Stack>
                          );
                        })
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Pendência / correção solicitada"
                    value={reviewForm.pendencia_motivo}
                    onChange={(e) =>
                      setReviewForm((prev) => ({ ...prev, pendencia_motivo: e.target.value }))
                    }
                    multiline
                    minRows={3}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Observações internas"
                    value={reviewForm.observacoes_internas}
                    onChange={(e) =>
                      setReviewForm((prev) => ({ ...prev, observacoes_internas: e.target.value }))
                    }
                    multiline
                    minRows={3}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Agendamento da integração"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={reviewForm.integracao_agendada_para}
                    onChange={(e) =>
                      setReviewForm((prev) => ({
                        ...prev,
                        integracao_agendada_para: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Link de Documentação (URL para guia/procedimento)"
                    value={reviewForm.link_documentacao || ''}
                    onChange={(e) =>
                      setReviewForm((prev) => ({ ...prev, link_documentacao: e.target.value }))
                    }
                    placeholder="https://..."
                    helperText="Cole a URL do procedimento ou guia de validação"
                  />
                </Grid>
              </Grid>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} flexWrap="wrap" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="info"
                  onClick={salvarAnalise}
                  disabled={Boolean(busyAction)}
                >
                  Salvar Análise
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  onClick={executarValidacao}
                  disabled={Boolean(busyAction)}
                >
                  Validar Cadastro
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={carregarHistorico}
                  disabled={Boolean(busyAction)}
                >
                  Ver Histórico
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => updateStatus('em_analise')}
                  disabled={Boolean(busyAction)}
                  startIcon={<FactCheck />}
                >
                  Em análise
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => updateStatus('pendente_correcao')}
                  disabled={Boolean(busyAction)}
                >
                  Pendenciar correção
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => updateStatus('aprovado')}
                  disabled={Boolean(busyAction)}
                  startIcon={<CheckCircleOutline />}
                >
                  Aprovar cadastro
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => updateStatus('integracao_agendada')}
                  disabled={Boolean(busyAction)}
                  startIcon={<GroupAdd />}
                >
                  Agendar integração
                </Button>
                <Button
                  variant="contained"
                  onClick={() => updateStatus('liberado')}
                  disabled={Boolean(busyAction)}
                >
                  Liberar início das atividades
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Storefront />}
                  onClick={() => convertToFornecedor()}
                  disabled={Boolean(busyAction) || Boolean(selectedItem.fornecedor_id)}
                >
                  {selectedItem.fornecedor_id ? 'Fornecedor criado' : 'Cadastrar em fornecedores'}
                </Button>
              </Stack>

              {historicoOpen && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Histórico de Alterações ({historico.length} evento(s))
                  </Typography>
                  {historico.length === 0 ? (
                    <Typography color="text.secondary">Nenhum evento registrado ainda.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {historico.map((evento, idx) => (
                        <Paper
                          key={idx}
                          variant="outlined"
                          sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'background.default' }}
                        >
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} justifyContent="space-between">
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={labelTipoHistorico(evento.tipo_mudanca)}
                                  color={corTipoHistorico(evento.tipo_mudanca)}
                                  variant="outlined"
                                />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {descricaoHistorico(evento)}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(evento.criado_em).toLocaleString('pt-BR')}
                              </Typography>
                            </Stack>
                            {evento.usuario_nome && (
                              <Typography variant="caption" color="text.secondary">
                                Por: {evento.usuario_nome}
                              </Typography>
                            )}
                            {evento.observacoes && (
                              <Typography variant="body2" color="text.secondary">
                                {evento.observacoes}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Paper>
              )}

              {validacao.resultado && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Resultado da Validação
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(validacao.resultado).map(([chave, valor]) => (
                      <Grid item xs={12} sm={6} key={chave}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={chave.replace(/_/g, ' ')}
                            color={valor === true ? 'success' : valor === false ? 'error' : 'default'}
                            variant={valor === true ? 'filled' : 'outlined'}
                          />
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de excluídos — somente admin */}
      <Dialog open={deletedOpen} onClose={() => setDeletedOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete color="error" />
            Processos Excluídos
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDeleted ? (
            <Typography sx={{ py: 2 }}>Carregando...</Typography>
          ) : deletedItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Nenhum processo excluído.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Protocolo</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>Excluído em</TableCell>
                  <TableCell>Por</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{item.protocolo}</TableCell>
                    <TableCell>{item.razao_social}</TableCell>
                    <TableCell>{item.cnpj || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {item.deleted_at ? new Date(item.deleted_at).toLocaleString('pt-BR') : '-'}
                    </TableCell>
                    <TableCell>{item.deleted_by_name || '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Restaurar processo">
                        <Button size="small" variant="outlined" color="success" onClick={() => handleRestore(item)}>
                          Restaurar
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletedOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de edição — somente admin */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar Processo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Razão Social"
              value={editForm.razao_social}
              onChange={(e) => setEditForm((f) => ({ ...f, razao_social: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              >
                {Object.entries(statusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Integração</InputLabel>
              <Select
                value={editForm.integracao_status}
                label="Integração"
                onChange={(e) => setEditForm((f) => ({ ...f, integracao_status: e.target.value }))}
              >
                <MenuItem value="pendente">Pendente</MenuItem>
                <MenuItem value="concluida">Concluída</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editBusy}>
            {editBusy ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
